import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { Spin } from "antd";
import { useNavigate } from "react-router-dom";
import {
  getActiveProjectId,
  getStoredProjectId,
  setActiveProjectId,
} from "../helper/axios_helper";
import {
  getProjects,
  getDefaultProject,
  getProjectIsAdmin,
  setDefaultProject as apiSetDefaultProject,
} from "../service/project.service";

const ProjectContext = createContext(null);

/**
 * ProjectProvider — owns the active project for the session.
 *
 * Mount this INSIDE the authenticated tree (App.js renders it around HomePage),
 * never above the login screen: bootstrap calls /api/auth/* and would 401.
 *
 * Ordering matters. Children are not rendered until bootstrap has resolved and
 * setActiveProjectId() has run, so the X-Project-Id header is attached before
 * any page's fetch effect fires. (Child effects run before parent effects in
 * React, so syncing the header from a parent useEffect would be one render too
 * late and the first request of every page would go out unscoped.)
 */
export function ProjectProvider({ children }) {
  const [projects, setProjects] = useState([]);
  const [activeProjectId, setActive] = useState(null);
  const [defaultProjectId, setDefaultId] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [ready, setReady] = useState(false);
  // Safe: ProjectProvider is mounted inside <BrowserRouter> (index.js → App.js).
  const navigate = useNavigate();

  // Sets the axios header synchronously, then the React state.
  const applyActiveProject = useCallback((projectId) => {
    setActiveProjectId(projectId);
    setActive(projectId);
  }, []);

  const loadProjects = useCallback(async () => {
    const res = await getProjects();
    const list = res.data || [];
    setProjects(list);
    return list;
  }, []);

  useEffect(() => {
    let cancelled = false;

    const bootstrap = async () => {
      try {
        const [listRes, defaultRes] = await Promise.all([
          getProjects(),
          getDefaultProject(),
        ]);
        if (cancelled) return;

        const list = listRes.data || [];
        const serverDefault = defaultRes.data?.defaultProjectId || null;

        // A session switch survives a page refresh; a fresh login does not.
        const stored = getStoredProjectId();
        const selectable = (id) =>
          !!id && list.some((p) => p.projectId === id && p.active);

        const chosen = selectable(stored)
          ? stored
          : selectable(serverDefault)
            ? serverDefault
            : (list.find((p) => p.active)?.projectId ?? null);

        setProjects(list);
        setDefaultId(serverDefault);
        applyActiveProject(chosen);
      } catch {
        // Leave the app usable — the backend falls back to the default project
        // when no header is sent.
      }

      // is-admin is cosmetic (it only reveals admin-only actions); a failure
      // must not block the app, and the backend gates the write endpoints anyway.
      try {
        const adminRes = await getProjectIsAdmin();
        if (!cancelled) setIsAdmin(!!adminRes.data?.isAdmin);
      } catch {
        if (!cancelled) setIsAdmin(false);
      }

      if (!cancelled) setReady(true);
    };

    bootstrap();
    return () => {
      cancelled = true;
    };
  }, [applyActiveProject]);

  /**
   * Transient — does not change the user's stored default.
   *
   * Switching project changes the whole data scope, so the current route (and any
   * record id carried in location.state) belongs to the project you just left.
   * Land on the Dashboard, and `replace` the history entry so Back can't return to
   * a record that no longer resolves under the new project.
   *
   * `redirect: false` is for callers already on a project-management screen that
   * should stay put (see admin/projects/Projects.js on delete).
   */
  const switchProject = useCallback(
    (projectId, { redirect = true } = {}) => {
      if (!projectId || projectId === getActiveProjectId()) return;
      applyActiveProject(projectId);
      if (redirect) navigate("/dashboard", { replace: true });
    },
    [applyActiveProject, navigate],
  );

  /** Persists the caller's landing project (user resolved server-side). */
  const setAsDefaultProject = useCallback(async (projectId) => {
    await apiSetDefaultProject(projectId);
    setDefaultId(projectId);
  }, []);

  const activeProject = useMemo(
    () => projects.find((p) => p.projectId === activeProjectId) || null,
    [projects, activeProjectId],
  );

  const value = useMemo(
    () => ({
      projects,
      activeProject,
      activeProjectId,
      defaultProjectId,
      isAdmin,
      switchProject,
      setAsDefaultProject,
      refreshProjects: loadProjects,
    }),
    [
      projects,
      activeProject,
      activeProjectId,
      defaultProjectId,
      isAdmin,
      switchProject,
      setAsDefaultProject,
      loadProjects,
    ],
  );

  if (!ready) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <Spin size="large" />
      </div>
    );
  }

  return (
    <ProjectContext.Provider value={value}>{children}</ProjectContext.Provider>
  );
}

/**
 * useProject — the active project anywhere in the authenticated app.
 *
 * const { activeProject, activeProjectId, projects, switchProject } = useProject();
 *
 * Pages never send the project themselves — axios attaches X-Project-Id.
 */
export function useProject() {
  const context = useContext(ProjectContext);
  if (!context) throw new Error("useProject must be used within ProjectProvider");
  return context;
}

export default ProjectContext;
